import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Row, Col, Form, InputGroup, Button, Spinner } from "react-bootstrap";
import { Alert } from "@mui/material";
import DataTable from "../DataTable";
import { getPaymentsDashboardData } from "../utils";
import { FaMoneyBillWave, FaWallet, FaArrowDown, FaClock, FaUsers, FaSearch, FaEye } from "react-icons/fa";

export default function PaymentsDashboard() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [dashboardData, setDashboardData] = useState({ owners: [], summary: {} });
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [dateFilter, setDateFilter] = useState("all");

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError("");
      const data = await getPaymentsDashboardData();
      setDashboardData(data);
    } catch (err) {
      console.error("Error fetching dashboard data:", err);
      setError("Failed to load payments dashboard data. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amountInCents) => {
    const amount = (amountInCents || 0) / 100;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const filteredOwners = useMemo(() => {
    let filtered = dashboardData.owners || [];

    // Search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(owner =>
        owner.name.toLowerCase().includes(term) ||
        owner.email.toLowerCase().includes(term) ||
        owner.id.toLowerCase().includes(term)
      );
    }

    // Status filter
    if (statusFilter !== "all") {
      if (statusFilter === "with-balance") {
        filtered = filtered.filter(owner => owner.currentBalance > 0);
      } else if (statusFilter === "no-balance") {
        filtered = filtered.filter(owner => owner.currentBalance === 0);
      } else if (statusFilter === "pending-withdrawals") {
        filtered = filtered.filter(owner => owner.pendingWithdrawals > 0);
      }
    }

    // Date filter (based on last completed payment date)
    if (dateFilter !== "all") {
      const now = new Date();
      filtered = filtered.filter(owner => {
        if (!owner.lastCompletedPaymentDate) return dateFilter === "never";
        const lastDate = new Date(owner.lastCompletedPaymentDate);
        const daysDiff = (now - lastDate) / (1000 * 60 * 60 * 24);
        
        switch (dateFilter) {
          case "7days": return daysDiff <= 7;
          case "30days": return daysDiff <= 30;
          case "90days": return daysDiff <= 90;
          case "never": return false;
          default: return true;
        }
      });
    }

    return filtered;
  }, [dashboardData.owners, searchTerm, statusFilter, dateFilter]);

  const handleViewDetails = (ownerId) => {
    navigate(`/dashboard/payments-dashboard/${ownerId}`);
  };

  const columns = [
    { 
      Header: "Boat Owner", 
      accessor: "name",
      Cell: ({ row }) => (
        <div>
          <div className="fw-semibold">{row.original.name}</div>
          <small className="text-muted">{row.original.email}</small>
        </div>
      )
    },
    { 
      Header: "Total Earnings", 
      accessor: "totalEarnings",
      Cell: ({ value }) => (
        <span className="text-success fw-semibold">{formatCurrency(value)}</span>
      )
    },
    { 
      Header: "Total Withdrawals", 
      accessor: "totalWithdrawals",
      Cell: ({ value }) => (
        <span className="text-danger">{formatCurrency(value)}</span>
      )
    },
    { 
      Header: "Current Balance", 
      accessor: "currentBalance",
      Cell: ({ value }) => (
        <span className="text-primary fw-bold">{formatCurrency(value)}</span>
      )
    },
    {
      Header: "First Rental Fee",
      accessor: "firstRentalFee",
      Cell: ({ value }) => (
        value > 0
          ? <span className="text-warning fw-semibold">{formatCurrency(value)}</span>
          : <span className="text-muted">-</span>
      )
    },
    {
      Header: "BBR Commission",
      accessor: "bbrCommission",
      Cell: ({ value }) => (
        value > 0
          ? <span className="text-danger">{formatCurrency(value)}</span>
          : <span className="text-muted">-</span>
      )
    },
    {
      Header: "5% Platform Fee",
      accessor: "platformFee",
      Cell: ({ value }) => (
        <span className="text-info">{formatCurrency(value)}</span>
      )
    },
    {
      Header: "Tax",
      accessor: "tax",
      Cell: ({ value }) => (
        <span className="text-muted">{formatCurrency(value)}</span>
      )
    },
    { 
      Header: "Pending", 
      accessor: "pendingWithdrawals",
      Cell: ({ value }) => (
        value > 0 ? (
          <span className="badge bg-warning text-dark">{formatCurrency(value)}</span>
        ) : (
          <span className="text-muted">-</span>
        )
      )
    },
    { 
      Header: "Completed Trips",
      accessor: "completedPaymentsCount",
      Cell: ({ row }) => (
        <span>{row.original.completedPaymentsCount} completed / {row.original.withdrawalsCount} withdrawals</span>
      )
    },
    {
      Header: "Actions",
      accessor: "id",
      Cell: ({ value }) => (
        <Button 
          variant="outline-primary" 
          size="sm"
          onClick={(e) => {
            e.stopPropagation();
            handleViewDetails(value);
          }}
        >
          <FaEye className="me-1" /> View Details
        </Button>
      )
    }
  ];

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "400px" }}>
        <Spinner animation="border" variant="primary" />
        <span className="ms-3">Loading payments dashboard...</span>
      </div>
    );
  }

  return (
    <>
      <div className="row">
        <div className="col">
          <div className="page-description">
            <h1>Payments Dashboard</h1>
            <p className="text-muted">Overview of all boat owner earnings, withdrawals, and balances</p>
          </div>
        </div>
      </div>

      {error && (
        <Alert severity="error" className="mb-4">
          {error}
        </Alert>
      )}

      {/* Summary Cards */}
      <Row className="mb-4">
        <Col xs={12} sm={6} lg={3} className="mb-3">
          <div className="card h-100">
            <div className="card-body">
              <div className="d-flex align-items-center">
                <div className="rounded-circle p-3 bg-success bg-opacity-10 me-3">
                  <FaMoneyBillWave className="text-success" size={24} />
                </div>
                <div>
                  <h6 className="text-muted mb-1">Total Payments</h6>
                  <h4 className="mb-0 text-success">{formatCurrency(dashboardData.summary?.totalPayments)}</h4>
                </div>
              </div>
            </div>
          </div>
        </Col>
        <Col xs={12} sm={6} lg={3} className="mb-3">
          <div className="card h-100">
            <div className="card-body">
              <div className="d-flex align-items-center">
                <div className="rounded-circle p-3 bg-danger bg-opacity-10 me-3">
                  <FaArrowDown className="text-danger" size={24} />
                </div>
                <div>
                  <h6 className="text-muted mb-1">Total Withdrawals</h6>
                  <h4 className="mb-0 text-danger">{formatCurrency(dashboardData.summary?.totalWithdrawals)}</h4>
                </div>
              </div>
            </div>
          </div>
        </Col>
        <Col xs={12} sm={6} lg={3} className="mb-3">
          <div className="card h-100">
            <div className="card-body">
              <div className="d-flex align-items-center">
                <div className="rounded-circle p-3 bg-primary bg-opacity-10 me-3">
                  <FaWallet className="text-primary" size={24} />
                </div>
                <div>
                  <h6 className="text-muted mb-1">Total Balance</h6>
                  <h4 className="mb-0 text-primary">{formatCurrency(dashboardData.summary?.totalBalance)}</h4>
                </div>
              </div>
            </div>
          </div>
        </Col>
        <Col xs={12} sm={6} lg={3} className="mb-3">
          <div className="card h-100">
            <div className="card-body">
              <div className="d-flex align-items-center">
                <div className="rounded-circle p-3 bg-warning bg-opacity-10 me-3">
                  <FaClock className="text-warning" size={24} />
                </div>
                <div>
                  <h6 className="text-muted mb-1">Pending Withdrawals</h6>
                  <h4 className="mb-0 text-warning">{formatCurrency(dashboardData.summary?.pendingWithdrawals)}</h4>
                </div>
              </div>
            </div>
          </div>
        </Col>
      </Row>

      {/* Boat Owners Count */}
      <Row className="mb-4">
        <Col>
          <div className="d-flex align-items-center">
            <FaUsers className="text-muted me-2" />
            <span className="text-muted">
              {dashboardData.summary?.totalOwners || 0} Boat Owners
            </span>
          </div>
        </Col>
      </Row>

      {/* Filters */}
      <Row className="mb-4">
        <Col xs={12} md={4} className="mb-3 mb-md-0">
          <InputGroup>
            <InputGroup.Text>
              <FaSearch />
            </InputGroup.Text>
            <Form.Control
              type="text"
              placeholder="Search by name, email, or ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </InputGroup>
        </Col>
        <Col xs={12} sm={6} md={4} className="mb-3 mb-sm-0">
          <Form.Select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="all">All Statuses</option>
            <option value="with-balance">With Balance</option>
            <option value="no-balance">No Balance</option>
            <option value="pending-withdrawals">Pending Withdrawals</option>
          </Form.Select>
        </Col>
        <Col xs={12} sm={6} md={4}>
          <Form.Select
            value={dateFilter}
            onChange={(e) => setDateFilter(e.target.value)}
          >
            <option value="all">All Time</option>
            <option value="7days">Last 7 Days</option>
            <option value="30days">Last 30 Days</option>
            <option value="90days">Last 90 Days</option>
            <option value="never">No Payments</option>
          </Form.Select>
        </Col>
      </Row>

      {/* Data Table */}
      <div className="row">
        <div className="col">
          <div className="card">
            <div className="card-body">
              {filteredOwners.length === 0 ? (
                <div className="text-center py-5">
                  <FaUsers size={48} className="text-muted mb-3" />
                  <h5 className="text-muted">No boat owners found</h5>
                  <p className="text-muted">
                    {searchTerm || statusFilter !== "all" || dateFilter !== "all"
                      ? "Try adjusting your filters"
                      : "No boat owners have payment data yet"}
                  </p>
                </div>
              ) : (
                <DataTable
                  columns={columns}
                  data={filteredOwners}
                  onRowClick={(row) => handleViewDetails(row.original.id)}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
